// S15 Status view for one report (root 6.12, REQ-9.2, REQ-9.5 to REQ-9.7, REQ-13.4): type, stage
// history, outcome, fix note; Confirm fix / Reopen… only while Fixed; Withdraw only while open; the team's
// Reporter-visible messages and the person's own replies; Reply with files after the masking warning.
// Teams links open /my-requests/<publicId>?action=confirm|reopen: the matching dialog opens if the
// action still applies, otherwise the page says the report has moved on and changes nothing.
// A feature request (poc CT-004) shows its own stages (Backlog, Planned…), its outcome in the stage's
// place once finished (Shipped, Declined), and its release note; it's never fixed, so never confirmed.
// "← My requests" returns to the Show filter My requests last showed in this tab, else Pending (poc CT-006).
import type { AttachmentView } from '@feedback/shared/contracts/attachments';
import type { ReporterStatusView } from '@feedback/shared/contracts/query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { FileDropzone } from '@/components/FileDropzone';
import { LoadingState } from '@/components/LoadingState';
import { ReasonDialog } from '@/components/ReasonDialog';
import { StageBadge } from '@/components/StageBadge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { showSearch } from '@/features/my-requests/groups';
import { lastShownFilter } from '@/features/my-requests/last-show';
import { isFeatureRequest, reporterTypeLabel } from '@/features/tickets/labels';
import { FileList } from '@/features/tickets/TicketCapture';
import { api, isApiError, seg } from '@/lib/api';
import { formatDateTime, formatShortDateTime } from '@/lib/format';
import { loadSession } from '@/lib/session';
import { useReporterCommand } from '@/lib/ticket-commands';
import { cn } from '@/lib/utils';

type Action = 'confirm' | 'reopen';
const REPLY_MAX = 20000;
const MAX_FILES = 10;

export const Route = createFileRoute('/my-requests/$publicId')({
  // The key is always returned (undefined when missing or unknown), so a raw `?action=` can't survive.
  validateSearch: (search: Record<string, unknown>): { action?: Action } => ({
    action: search.action === 'confirm' || search.action === 'reopen' ? search.action : undefined,
  }),
  beforeLoad: ({ context, location }) => loadSession(context.queryClient, location.href),
  component: StatusPage,
});

function StatusPage() {
  const { publicId } = Route.useParams();
  const view = useQuery({
    queryKey: ['my-request', publicId],
    queryFn: () => api.get<ReporterStatusView>(`/api/my-requests/${seg(publicId)}`),
  });
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4">
      <Link
        to="/my-requests"
        search={showSearch(lastShownFilter())}
        // Exact, so the status view never marks its back link as the current page.
        activeOptions={{ exact: true }}
        className="inline-flex w-fit items-center gap-1 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        My requests
      </Link>
      {view.isPending ? (
        <LoadingState label="Loading your report" />
      ) : view.isError ? (
        isApiError(view.error) && view.error.status === 404 ? (
          <EmptyState title="We couldn’t find this report">
            Status links open only for the person who reported it. Check you’re signed in with the right account.
          </EmptyState>
        ) : (
          <ErrorState error={view.error} onRetry={() => void view.refetch()} />
        )
      ) : (
        <StatusView view={view.data} />
      )}
    </div>
  );
}

function StatusView({ view }: { view: ReporterStatusView }) {
  const { action } = Route.useSearch();
  const navigate = Route.useNavigate();
  const command = useReporterCommand(view);
  const [open, setOpen] = useState<'confirm' | 'reopen' | 'withdraw' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The outcome (Shipped, Declined, Won't fix…) stands in for a finished stage's name.
  const shownStage = view.outcome?.name ?? view.stage.name;

  // A Teams link's action: open its dialog once, if it still applies.
  useEffect(() => {
    if (!action) {
      return;
    }
    if (view.actions[action]) {
      setOpen(action);
    } else {
      setNotice(
        `This report is now ${shownStage}, so there’s nothing to ${action === 'confirm' ? 'confirm' : 'reopen'}. Nothing was changed.`,
      );
    }
    void navigate({ search: {}, replace: true });
  }, [action, view.actions, shownStage, navigate]);

  const onError = (error: unknown) => {
    setOpen(null);
    setNotice(
      isApiError(error) && error.status === 409
        ? `This report has moved on${typeof error.problem.currentStageName === 'string' ? `: it’s now ${error.problem.currentStageName}` : ''}. Nothing was changed.`
        : isApiError(error)
          ? error.detail
          : 'Something went wrong. Try again.',
    );
  };
  const done = (text: string) => () => {
    setOpen(null);
    setNotice(null);
    toast.success(text);
  };

  return (
    <article className="flex flex-col gap-5">
      <header className="flex flex-col gap-2">
        <p className="font-mono text-sm text-muted">{view.reference ?? 'New report'}</p>
        <h1 className="text-2xl font-semibold break-words">{view.title}</h1>
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted">
          {view.type ? <span data-testid="request-type">{reporterTypeLabel(view.type)} ·</span> : null}
          <span>{view.outcome ? 'Outcome:' : 'Stage:'}</span>{' '}
          <StageBadge stage={{ code: view.stage.code, name: shownStage }} />
          <span>· {view.app.name}</span>
        </p>
        {view.outcome?.reason ? (
          <p className="text-sm">
            Why: <span className="font-medium">{view.outcome.reason}</span>
          </p>
        ) : null}
        {view.withdrawnAt ? (
          <p className="text-sm text-muted">You withdrew this report on {formatDateTime(view.withdrawnAt)}.</p>
        ) : null}
      </header>

      {notice ? (
        <p role="status" className="rounded-md border border-info p-3 text-sm">
          {notice}
        </p>
      ) : null}

      <section aria-labelledby="history-title">
        <h2 id="history-title" className="mb-2 font-semibold">
          What happened
        </h2>
        <ol className="flex flex-wrap items-center gap-x-1 gap-y-2 text-sm" data-testid="stage-history">
          {view.stageHistory.map((entry, index) => (
            <li key={`${entry.stage.code}-${entry.at}`} className="flex items-center gap-1">
              {index > 0 ? (
                <span aria-hidden="true" className="text-muted">
                  ›
                </span>
              ) : null}
              <span className={cn(index === view.stageHistory.length - 1 && 'font-semibold')}>{entry.stage.name}</span>
              <time dateTime={entry.at} title={formatDateTime(entry.at)} className="text-muted">
                {formatShortDateTime(entry.at)}
              </time>
            </li>
          ))}
        </ol>
      </section>

      {view.fixNote ? (
        <p className="rounded-md border border-border bg-surface p-3 text-sm">
          <span className="font-medium">{isFeatureRequest(view.type) ? 'Release note: ' : 'Fix: '}</span>“
          {view.fixNote.text}”
        </p>
      ) : null}

      {view.actions.confirm || view.actions.reopen ? (
        <div className="flex flex-wrap gap-2">
          {view.actions.confirm ? (
            <Button onClick={() => setOpen('confirm')}>
              <CheckCircle2 aria-hidden="true" />
              Confirm fix
            </Button>
          ) : null}
          {view.actions.reopen ? (
            <Button variant="secondary" onClick={() => setOpen('reopen')}>
              Reopen…
            </Button>
          ) : null}
        </div>
      ) : null}

      <section aria-labelledby="your-words">
        <h2 id="your-words" className="mb-2 font-semibold">
          What you reported
        </h2>
        <blockquote className="rounded-md border border-border p-3 text-sm whitespace-pre-wrap break-words">
          {view.originalText}
        </blockquote>
        <p className="mt-1 text-xs text-muted">Sent {formatDateTime(view.submittedAt)}</p>
        {view.files.length > 0 ? (
          <div className="mt-2">
            <FileList files={view.files} />
          </div>
        ) : null}
      </section>

      <Messages view={view} />
      {view.actions.reply ? <Reply view={view} /> : null}

      {view.actions.withdraw ? (
        <div>
          <Button variant="secondary" onClick={() => setOpen('withdraw')}>
            Withdraw report
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        open={open === 'confirm'}
        onOpenChange={(value) => setOpen(value ? 'confirm' : null)}
        title="Confirm the fix?"
        description="Tell the team it works for you now. The ticket closes."
        confirmLabel="Confirm fix"
        pending={command.isPending}
        onConfirm={() =>
          command.mutate({ type: 'confirm_fix' }, { onSuccess: done('Thanks, the fix is confirmed'), onError })
        }
      />
      <ReasonDialog
        open={open === 'reopen'}
        onOpenChange={(value) => setOpen(value ? 'reopen' : null)}
        title="Reopen this report"
        description="Tell the team what still doesn’t work. It goes back to them for review."
        label="What still doesn’t work"
        confirmLabel="Reopen"
        pending={command.isPending}
        onSubmit={(reason) =>
          command.mutate(
            { type: 'reopen', reason },
            { onSuccess: done('Reopened. The team will look at it again'), onError },
          )
        }
      />
      <ConfirmDialog
        open={open === 'withdraw'}
        onOpenChange={(value) => setOpen(value ? 'withdraw' : null)}
        title="Withdraw this report?"
        description="The team stops working on it for you. If others reported the same thing, their reports stay open."
        confirmLabel="Withdraw report"
        destructive
        pending={command.isPending}
        onConfirm={() =>
          command.mutate({ type: 'withdraw_report' }, { onSuccess: done('Your report is withdrawn'), onError })
        }
      />
    </article>
  );
}

function Messages({ view }: { view: ReporterStatusView }) {
  return (
    <section aria-labelledby="messages-title">
      <h2 id="messages-title" className="mb-2 font-semibold">
        Messages from the team
      </h2>
      {view.messages.length === 0 ? (
        <p className="text-sm text-muted">No messages yet.</p>
      ) : (
        <ol className="flex flex-col gap-2">
          {view.messages.map((m) => (
            <li
              key={m.id}
              className={cn(
                'rounded-md border p-3 text-sm',
                m.from === 'me' ? 'ml-6 border-border bg-surface' : 'mr-6 border-info',
              )}
            >
              <p className="text-xs text-muted">
                {m.from === 'me' ? 'You' : m.authorDisplayName}, {formatDateTime(m.createdAt)}
              </p>
              <p className="mt-1 whitespace-pre-wrap break-words">{m.body}</p>
              {m.attachments.length > 0 ? (
                <div className="mt-2">
                  <FileList files={m.attachments} />
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function Reply({ view }: { view: ReporterStatusView }) {
  const id = useId();
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<AttachmentView[]>([]);
  const command = useReporterCommand(view);
  const lastTeamMessage = [...view.messages].reverse().find((m) => m.from === 'team');
  const upload = useMutation({
    meta: { silent: true },
    mutationFn: (file: File) => api.upload<AttachmentView>(`/api/my-requests/${seg(view.publicId)}/attachments`, file),
    onSuccess: (stored) => setFiles((prev) => [...prev, stored]),
    onError: (error, file) => toast.error(`${file.name}: ${isApiError(error) ? error.detail : 'couldn’t be attached'}`),
  });
  return (
    <form
      className="flex flex-col gap-2 rounded-md border border-border p-3"
      onSubmit={(event) => {
        event.preventDefault();
        command.mutate(
          {
            type: 'reporter_reply',
            body: body.trim(),
            ...(files.length > 0 ? { attachmentIds: files.map((f) => f.id) } : {}),
            ...(lastTeamMessage ? { inReplyToCommentId: lastTeamMessage.id } : {}),
          },
          {
            onSuccess: () => {
              setBody('');
              setFiles([]);
              toast.success('Your reply is sent to the team');
            },
            onError: (error) =>
              toast.error(isApiError(error) ? error.detail : 'Your reply couldn’t be sent. Try again.'),
          },
        );
      }}
    >
      <Label htmlFor={`${id}-reply`}>Reply to the team</Label>
      <Textarea id={`${id}-reply`} value={body} maxLength={REPLY_MAX} onChange={(e) => setBody(e.target.value)} />
      {files.length > 0 ? <FileList files={files} /> : null}
      <div className="flex flex-wrap items-start gap-2">
        <FileDropzone
          warnUnmasked
          label={upload.isPending ? 'Attaching…' : 'Attach'}
          disabled={upload.isPending || files.length >= MAX_FILES}
          onFiles={(picked) => {
            for (const file of picked.slice(0, MAX_FILES - files.length)) {
              upload.mutate(file);
            }
          }}
          className="min-w-0 flex-1"
        />
        <Button type="submit" disabled={!body.trim() || command.isPending || upload.isPending}>
          Reply
        </Button>
      </div>
    </form>
  );
}
